import { CLIENT_NOTES_CHANGED_EVENT, readClientNotes, updateClientNotes, useClientNotes } from './clientNotesStore';
import type { ClientNotes } from './clientNotesStore';

/**
 * 招募階段 — where a candidate is in the recruitment funnel, set by the
 * recruiter from the 招募 header's 階段 pill.
 *
 * Stored on the server as `factsheet.recruitment.stage` (+ `stageUpdatedAt`), through the notes store (./clientNotesStore).
 */

export const RECRUIT_STAGES = ['notStarted', 'contact', 'talking', 'paused', 'exam', 'joined'] as const;
export type RecruitStage = (typeof RECRUIT_STAGES)[number];

/**
 * The six dots, picked for how far apart they read at 7px rather than for a
 * ramp. The four ON-PATH stages sit at least 50° apart on the colour wheel
 * (青 192° → 紫 262° → 桃紅 333° → 綠 142°), and the closest pair — 初步接觸's
 * cyan and 已入職's green — is deliberately the FIRST and LAST stage, so the
 * two never appear as neighbours in a sorted list.
 *
 * Two families are avoided outright: amber, because 招募模式's own accent
 * (`$clientv2-recruit-accent`) owns it, and the app's primary blue, which
 * already means "selected" everywhere else.
 */
export const RECRUIT_STAGE_COLORS: Record<RecruitStage, string> = {
  notStarted: '#cbd5e1',
  contact: '#0891b2',
  talking: '#7c3aed',
  paused: '#78716c',
  exam: '#db2777',
  joined: '#16a34a'
};

/**
 * Colour alone would not carry this set: 未開始 and 暫緩 are both "not moving",
 * and deuteranopia collapses 初步接觸's cyan towards 已入職's green. So the two
 * ends of the funnel are marked by SHAPE as well —
 *
 *   未開始  a hollow ring — nothing has happened yet, so the dot is empty
 *   已入職  a filled dot with a tick
 *
 * — which leaves 暫緩 as the only solid neutral, and makes 未開始 / 進行中 /
 * 已入職 separable with no colour vision at all.
 */
export const RECRUIT_STAGE_HOLLOW: RecruitStage = 'notStarted';
export const RECRUIT_STAGE_TICKED: RecruitStage = 'joined';

const isStage = (value: unknown): value is RecruitStage =>
  typeof value === 'string' && (RECRUIT_STAGES as readonly string[]).includes(value);

export const RECRUIT_STAGE_CHANGED_EVENT = CLIENT_NOTES_CHANGED_EVENT;

const stageOf = (notes: ClientNotes | null): RecruitStage | null => {
  const value = notes ? notes.recruitment.stage : null;
  return isStage(value) ? value : null;
};

export const readRecruitStage = (clientId: string): RecruitStage | null => stageOf(readClientNotes(clientId));

/** When the stage was last CHANGED — 暫緩太久 counts from here (§7). */
export const readRecruitStageUpdatedAt = (clientId: string): string | null =>
  readClientNotes(clientId)?.recruitment.stageUpdatedAt || null;

export const writeRecruitStage = (clientId: string | null | undefined, stage: RecruitStage | null) => {
  if (!clientId) return;
  void updateClientNotes(clientId, { recruitment: { stage } }, notes => {
    const recruitment = { ...notes.recruitment };
    const changed = (recruitment.stage || null) !== stage;
    if (stage) recruitment.stage = stage;
    else delete recruitment.stage;
    if (changed && stage) recruitment.stageUpdatedAt = new Date().toISOString();
    if (!stage) delete recruitment.stageUpdatedAt;
    return { ...notes, recruitment };
  });
};

export const useRecruitStage = (clientId: string | null | undefined) => stageOf(useClientNotes(clientId));
